/**
 * ContentBlockRenderer Component
 * 
 * Renders structured content blocks with automatic hyperlinks and interactive elements.
 * Supports both new structured content and legacy plain text content for backward compatibility.
 * 
 * Part of Phase 2B: Bidirectional Linking Architecture - Task P2B.2.1
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import './ContentBlockRenderer.css';

/**
 * Individual content block type renderers
 */

const TextBlock = ({ block }) => (
  <div className="content-block text-block" data-block-id={block.id}>
    <span className="text-content">{block.text}</span>
  </div>
);

/**
 * Helper to extract icon from text if present
 * @param {string} text - The text to parse
 * @param {string} defaultIcon - The default icon to use if none found
 * @returns {Object} { icon, text }
 */
const extractIconAndText = (text, defaultIcon) => {
  if (!text) return { icon: defaultIcon, text: '' };

  // Regex to match emoji at start of string followed by space
  // Matches generic emoji range and handles Variant Selector-16 (\uFE0F)
  // Also explicitly includes the specific icons used in the app to be safe
  const emojiRegex = /^([\p{Extended_Pictographic}\uFE0F]+)\s+(.*)/u;
  const match = text.match(emojiRegex);

  if (match) {
    return {
      icon: match[1],
      text: match[2]
    };
  }

  return {
    icon: defaultIcon,
    text: text
  };
};

const AccommodationBlock = ({ block, onAccommodationClick, onPlaceClick, onUnlinkAccommodation, tripId }) => {
  const navigate = useNavigate();

  const handleAccommodationClick = (e) => {
    e.preventDefault();
    if (onAccommodationClick) {
      onAccommodationClick(block.accommodationId);
    } else {
      navigate(`/trips/${tripId}/accommodation/${block.accommodationId}`);
    }
  };

  const handleUnlinkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onUnlinkAccommodation(block.accommodationId, block.accommodationName || 'this accommodation');
  };

  const handlePlaceClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onPlaceClick) {
      onPlaceClick(block.placeId);
    } else {
      navigate(`/trips/${tripId}/places/${block.placeId}`);
    }
  };

  const getContextDisplay = (context) => {
    switch (context) {
    case 'check-in': return '';
    case 'check-out': return '';
    case 'single-day': return '';
    case 'staying': return '';
    default: return '';
    }
  };

  // Parse displayText to extract components
  const parseDisplayText = (displayText) => {
    // First strip any leading icon if present
    const { text: cleanText } = extractIconAndText(displayText, '🏨');

    const match = cleanText.match(/^Stay at ([^:]+?)(?:: ([^\n(]+?))?(?:\s*\(([^)]+)\))?$/);

    if (match) {
      return {
        accommodationName: match[1]?.trim() || '',
        location: match[2]?.trim() || '',
        contextFromText: match[3]?.trim() || ''
      };
    }

    return {
      accommodationName: block.accommodationName || cleanText,
      location: '',
      contextFromText: ''
    };
  };

  const { accommodationName, location } = parseDisplayText(block.displayText);

  return (
    <div className="content-block accommodation-block" data-block-id={block.id}>
      <span className="accommodation-icon" aria-hidden="true">🏨</span>
      <span className="accommodation-block-content">
        <span className="accommodation-prefix">Stay at </span>
        <a
          href={`/trips/${tripId}/accommodation/${block.accommodationId}`}
          onClick={handleAccommodationClick}
          className="accommodation-link"
          aria-label={`Navigate to accommodation: ${accommodationName}`}
          title={'Click to view accommodation details'}
        >
          {accommodationName}
        </a>
        {location && (
          <span className="accommodation-location">
            : {block.placeId ? (
              <a
                href={`/trips/${tripId}/places/${block.placeId}`}
                onClick={handlePlaceClick}
                className="place-link"
                aria-label={`Navigate to place: ${location}`}
                title={`Click to view place details for ${location}`}
              >
                {location}
              </a>
            ) : (
              location
            )}
          </span>
        )}
        {block.context && getContextDisplay(block.context) && (
          <span className="context-indicator" aria-label={`Context: ${block.context}`}>
            {' '}{getContextDisplay(block.context)}
          </span>
        )}
      </span>
      {onUnlinkAccommodation && (
        <button
          type="button"
          className="accommodation-unlink-btn"
          onClick={handleUnlinkClick}
          title="Remove this stay from the day"
          aria-label={`Remove stay at ${accommodationName} from this day`}
        >
          <FaTimes />
        </button>
      )}
    </div>
  );
};

const TravelBlock = ({ block, onTravelClick, tripId }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    if (onTravelClick) {
      onTravelClick(block.travelId);
    } else {
      navigate(`/trips/${tripId}/travel/${block.travelId}`);
    }
  };

  const { icon, text } = extractIconAndText(block.displayText, '✈️');

  return (
    <div className="content-block travel-block" data-block-id={block.id}>
      <span className="travel-icon" aria-hidden="true">{icon}</span>
      <a
        href={`/trips/${tripId}/travel/${block.travelId}`}
        onClick={handleClick}
        className="travel-link"
        aria-label={`Navigate to travel: ${text}`}
        title={'Click to view travel details'}
      >
        {text}
      </a>
      {block.context && (
        <span className="context-indicator" aria-label={`Context: ${block.context}`}>
          {' '}({block.context})
        </span>
      )}
    </div>
  );
};

const ActivityBlock = ({ block, onActivityClick, tripId }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    if (onActivityClick) {
      onActivityClick(block.activityId);
    } else {
      navigate(`/trips/${tripId}/activities/${block.activityId}`);
    }
  };

  const { icon, text } = extractIconAndText(block.displayText, '🎯');

  return (
    <div className="content-block activity-block" data-block-id={block.id}>
      <span className="activity-icon" aria-hidden="true">{icon}</span>
      <a
        href={`/trips/${tripId}/activities/${block.activityId}`}
        onClick={handleClick}
        className="activity-link"
        aria-label={`Navigate to activity: ${text}`}
        title={'Click to view activity details'}
      >
        {text}
      </a>
      {block.context && (
        <span className="context-indicator" aria-label={`Context: ${block.context}`}>
          {' '}({block.context})
        </span>
      )}
    </div>
  );
};

const PlaceBlock = ({ block, onPlaceClick, tripId }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    if (onPlaceClick) {
      onPlaceClick(block.placeId);
    } else {
      navigate(`/trips/${tripId}/places/${block.placeId}`);
    }
  };

  const { icon, text } = extractIconAndText(block.displayText, '📍');

  return (
    <div className="content-block place-block" data-block-id={block.id}>
      <span className="place-icon" aria-hidden="true">{icon}</span>
      <a
        href={`/trips/${tripId}/places/${block.placeId}`}
        onClick={handleClick}
        className="place-link"
        aria-label={`Navigate to place: ${text}`}
        title={'Click to view place details'}
      >
        {text}
      </a>
      {block.context && (
        <span className="context-indicator" aria-label={`Context: ${block.context}`}>
          {' '}({block.context})
        </span>
      )}
    </div>
  );
};

const DayBlock = ({ block, onDayClick, tripId }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    if (onDayClick) {
      onDayClick(block.dayId);
    } else {
      navigate(`/trips/${tripId}/days/${block.dayId}`);
    }
  };

  const { icon, text } = extractIconAndText(block.displayText, '📅');

  return (
    <div className="content-block day-block" data-block-id={block.id}>
      <span className="day-icon" aria-hidden="true">{icon}</span>
      <a
        href={`/trips/${tripId}/days/${block.dayId}`}
        onClick={handleClick}
        className="day-link"
        aria-label={`Navigate to day: ${text}`}
        title={'Click to view day details'}
      >
        {text}
      </a>
      {block.date && (
        <span className="date-indicator" aria-label={`Date: ${block.date}`}>
          {' '}({block.date})
        </span>
      )}
    </div>
  );
};

const DocumentBlock = ({ block, onDocumentClick, tripId }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    if (onDocumentClick) {
      onDocumentClick(block.documentId);
    } else {
      navigate(`/trips/${tripId}/documents/${block.documentId}`);
    }
  };

  const { icon, text } = extractIconAndText(block.displayText, '📄');

  return (
    <div className="content-block document-block" data-block-id={block.id}>
      <span className="document-icon" aria-hidden="true">{icon}</span>
      <a
        href={`/trips/${tripId}/documents/${block.documentId}`}
        onClick={handleClick}
        className="document-link"
        aria-label={`Navigate to document: ${text}`}
        title={'Click to view document details'}
      >
        {text}
      </a>
      {block.connectionType && (
        <span className="connection-type-indicator" aria-label={`Connection type: ${block.connectionType}`}>
          {' '}({block.connectionType})
        </span>
      )}
    </div>
  );
};

/**
 * Renders a single content block based on its type
 */
const renderContentBlock = (block, handlers, tripId) => {
  if (!block || !block.type) {
    console.warn('ContentBlockRenderer: Invalid block structure', block);
    return null;
  }

  const key = block.id || `block-${Math.random()}`;

  switch (block.type) {
  case 'text':
    return <TextBlock key={key} block={block} />;

  case 'accommodation_reference':
    return (
      <AccommodationBlock
        key={key}
        block={block}
        onAccommodationClick={handlers.onAccommodationClick}
        onPlaceClick={handlers.onPlaceClick}
        onUnlinkAccommodation={handlers.onUnlinkAccommodation}
        tripId={tripId}
      />
    );

  case 'travel_reference':
    return (
      <TravelBlock
        key={key}
        block={block}
        onTravelClick={handlers.onTravelClick}
        tripId={tripId}
      />
    );

  case 'activity_reference':
    return (
      <ActivityBlock
        key={key}
        block={block}
        onActivityClick={handlers.onActivityClick}
        tripId={tripId}
      />
    );

  case 'place_reference':
    return (
      <PlaceBlock
        key={key}
        block={block}
        onPlaceClick={handlers.onPlaceClick}
        tripId={tripId}
      />
    );

  case 'day_reference':
    return (
      <DayBlock
        key={key}
        block={block}
        onDayClick={handlers.onDayClick}
        tripId={tripId}
      />
    );

  case 'document_reference':
    return (
      <DocumentBlock
        key={key}
        block={block}
        onDocumentClick={handlers.onDocumentClick}
        tripId={tripId}
      />
    );

  default:
    console.warn(`ContentBlockRenderer: Unknown block type: ${block.type}`);
    return (
      <div key={key} className="content-block unknown-block">
        <span className="unknown-content">{block.displayText || block.text || 'Unknown content'}</span>
      </div>
    );
  }
};

/**
 * Detects if content is structured (array of content blocks) or legacy (string)
 */
const isStructuredContent = (content) => {
  return Array.isArray(content) && content.length > 0 &&
    content[0] && typeof content[0] === 'object' && content[0].type;
};

/**
 * Renders legacy content with basic accommodation link detection
 */
const LegacyContentRenderer = ({ content, handlers, tripId }) => {
  if (!content || typeof content !== 'string') {
    return <div className="legacy-content empty">No content for this day.</div>;
  }

  // Simple accommodation reference detection for legacy content
  // This provides basic linking even for legacy content
  const accommodationRegex = /Stay at ([^:\n]+)(?:: ([^(\n]+))?(?:\s*\(([^)]+)\))?/g;

  const renderWithLinks = (text) => {
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = accommodationRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {text.substring(lastIndex, match.index)}
          </span>
        );
      }

      // Add the accommodation link
      const [fullMatch, name, location, context] = match;
      parts.push(
        <span key={`accommodation-${match.index}`} className="legacy-accommodation-link">
          <span className="accommodation-icon" aria-hidden="true">🏨</span>
          <span className="accommodation-text">
            Stay at {name}
            {location && `: ${location}`}
            {context && ` (${context})`}
          </span>
        </span>
      );

      lastIndex = match.index + fullMatch.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {text.substring(lastIndex)}
        </span>
      );
    }

    return parts.length > 0 ? parts : [<span key="full-text">{text}</span>];
  };

  return (
    <div className="legacy-content">
      <div className="legacy-content-text">
        {renderWithLinks(content)}
      </div>
    </div>
  );
};

/**
 * Main ContentBlockRenderer Component
 * 
 * @param {Array|string} content - Either structured content blocks or legacy text
 * @param {Function} onAccommodationClick - Handler for accommodation link clicks
 * @param {Function} onTravelClick - Handler for travel link clicks  
 * @param {Function} onActivityClick - Handler for activity link clicks
 * @param {Function} onPlaceClick - Handler for place link clicks
 * @param {string} tripId - Current trip ID for navigation
 * @param {string} className - Additional CSS classes
 */
const ContentBlockRenderer = ({
  content,
  onAccommodationClick,
  onTravelClick,
  onActivityClick,
  onPlaceClick,
  onUnlinkAccommodation,
  tripId,
  className = ''
}) => {
  const handlers = {
    onAccommodationClick,
    onTravelClick,
    onActivityClick,
    onPlaceClick,
    onUnlinkAccommodation
  };

  // Handle empty content
  if (!content) {
    return (
      <div className={`content-renderer empty ${className}`}>
        <div className="empty-content">No content for this day.</div>
      </div>
    );
  }

  // Determine content type and render accordingly
  const isStructured = isStructuredContent(content);

  if (isStructured) {
    return (
      <div className={`content-renderer structured ${className}`}>
        <div className="content-blocks">
          {content.map(block => renderContentBlock(block, handlers, tripId))}
        </div>
      </div>
    );
  } else {
    return (
      <div className={`content-renderer legacy ${className}`}>
        <LegacyContentRenderer
          content={content}
          handlers={handlers}
          tripId={tripId}
        />
      </div>
    );
  }
};

export default ContentBlockRenderer; 